
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Button from '@/components/base/Button';

interface BlogPostData {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  image: string;
  tags: string[];
}

const blogPosts: BlogPostData[] = [
  {
    id: 'landing-page-elements',
    title: '10 Essential Elements Every High-Converting Landing Page Must Have',
    excerpt: 'Discover the key components that turn visitors into customers and boost your conversion rates.',
    content: `
      <h2>Introduction</h2>
      <p>Creating a high-converting landing page is both an art and a science. After analyzing thousands of successful landing pages and working with clients who've generated millions in revenue, I've identified 10 essential elements that every high-converting landing page must have.</p>
      
      <h2>1. Compelling Headline</h2>
      <p>Your headline is the first thing visitors see, and it determines whether they stay or leave. A great headline should:</p>
      <ul>
        <li>Clearly communicate your value proposition</li>
        <li>Address your target audience's pain point</li>
        <li>Be specific and benefit-focused</li>
        <li>Create urgency or curiosity</li>
      </ul>
      
      <h2>2. Strong Value Proposition</h2>
      <p>Your value proposition explains why someone should choose your product or service over competitors. It should be prominently displayed and answer the question: "What's in it for me?"</p>
      
      <h2>3. Social Proof</h2>
      <p>People trust other people more than they trust companies. Include:</p>
      <ul>
        <li>Customer testimonials with photos</li>
        <li>Client logos and case studies</li>
        <li>Review scores and ratings</li>
        <li>User-generated content</li>
      </ul>
      
      <h2>4. Clear Call-to-Action (CTA)</h2>
      <p>Your CTA button should stand out visually and use action-oriented language. Test different colors, sizes, and copy to optimize performance.</p>
      
      <h2>5. Benefits-Focused Copy</h2>
      <p>Focus on benefits rather than features. Show visitors how your product or service will improve their lives or solve their problems.</p>
      
      <h2>6. Visual Hierarchy</h2>
      <p>Guide visitors' eyes through your page using proper visual hierarchy. Use size, color, and spacing to direct attention to the most important elements.</p>
      
      <h2>7. Trust Signals</h2>
      <p>Build credibility with security badges, guarantees, certifications, and professional design elements.</p>
      
      <h2>8. Mobile Optimization</h2>
      <p>With over 50% of traffic coming from mobile devices, your landing page must look and function perfectly on all screen sizes.</p>
      
      <h2>9. Fast Loading Speed</h2>
      <p>Page speed directly impacts conversion rates. Aim for loading times under 3 seconds to keep visitors engaged.</p>
      
      <h2>10. A/B Testing Framework</h2>
      <p>Continuously test different elements to improve performance. Small changes can lead to significant improvements in conversion rates.</p>
      
      <h2>Conclusion</h2>
      <p>Implementing these 10 elements will significantly improve your landing page performance. Remember, conversion optimization is an ongoing process that requires continuous testing and refinement.</p>
    `,
    author: 'Sarah Johnson',
    date: '2024-01-15',
    readTime: '8 min read',
    category: 'Conversion Optimization',
    image: 'https://readdy.ai/api/search-image?query=Modern%20website%20landing%20page%20design%20on%20computer%20screen%2C%20clean%20user%20interface%2C%20conversion%20optimization%20elements%2C%20professional%20web%20design%20showcase&width=800&height=400&seq=blog-detail-1&orientation=landscape',
    tags: ['Landing Pages', 'Conversion', 'UX Design', 'Marketing']
  },
  {
    id: 'funnel-strategy-guide',
    title: 'The Complete Guide to Sales Funnel Strategy for 2024',
    excerpt: 'Learn how to build sales funnels that convert prospects into loyal customers.',
    content: `
      <h2>What is a Sales Funnel?</h2>
      <p>A sales funnel is a marketing concept that maps out the journey a customer goes through when making any kind of purchase. The model uses a funnel as an analogy because a large number of potential customers may begin at the top-end of the sales process, but only a fraction of these people actually end up making a purchase.</p>
      
      <h2>The Stages of a Sales Funnel</h2>
      <h3>1. Awareness Stage</h3>
      <p>At this stage, potential customers become aware of your product or service. They might discover you through:</p>
      <ul>
        <li>Social media posts</li>
        <li>Blog articles</li>
        <li>Advertisements</li>
        <li>Word of mouth</li>
      </ul>
      
      <h3>2. Interest Stage</h3>
      <p>Prospects show interest in your offering and want to learn more. They might:</p>
      <ul>
        <li>Subscribe to your newsletter</li>
        <li>Follow your social media accounts</li>
        <li>Download a free resource</li>
        <li>Attend a webinar</li>
      </ul>
      
      <h3>3. Decision Stage</h3>
      <p>Prospects are evaluating whether to purchase your product or service. They're comparing options and looking for the best solution.</p>
      
      <h3>4. Action Stage</h3>
      <p>The prospect becomes a customer by making a purchase. But the funnel doesn't end here – you want to turn customers into repeat buyers and advocates.</p>
      
      <h2>Building Your Sales Funnel Strategy</h2>
      <p>Creating an effective sales funnel requires understanding your audience, mapping their journey, and providing value at each stage.</p>
      
      <h2>Measuring Funnel Performance</h2>
      <p>Track key metrics like conversion rates at each stage, customer acquisition cost, and lifetime value to optimize your funnel performance.</p>
    `,
    author: 'Mike Chen',
    date: '2024-01-12',
    readTime: '12 min read',
    category: 'Sales Funnels',
    image: 'https://readdy.ai/api/search-image?query=Sales%20funnel%20diagram%20with%20multiple%20stages%2C%20conversion%20analytics%2C%20customer%20journey%20visualization%2C%20professional%20business%20graphics&width=800&height=400&seq=blog-detail-2&orientation=landscape',
    tags: ['Sales Funnels', 'Strategy', 'Marketing', 'Analytics']
  }
];

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPostData[]>([]);

  useEffect(() => {
    const foundPost = blogPosts.find(p => p.id === slug);
    if (foundPost) {
      setPost(foundPost);
      // Get related posts from same category
      const related = blogPosts.filter(p => p.id !== slug && p.category === foundPost.category).slice(0, 2);
      setRelatedPosts(related);
    } else {
      navigate('/blog');
    }
  }, [slug, navigate]);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      
      <main className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back button */}
          <button 
            onClick={() => navigate('/blog')}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-8 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Blog
          </button>

          {/* Article header */}
          <article>
            <header className="mb-8">
              <div className="flex items-center gap-4 mb-4">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  {post.category}
                </span>
                <span className="text-gray-500 text-sm">{post.readTime}</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                {post.title}
              </h1>
              
              <div className="flex items-center gap-4 text-gray-600 mb-8">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold mr-3">
                    {post.author.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{post.author}</p>
                    <p className="text-sm text-gray-500">{new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                </div>
              </div>
              
              <img 
                src={post.image} 
                alt={post.title}
                className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
              />
            </header>

            {/* Article content */}
            <div 
              className="prose prose-lg max-w-none mb-12"
              dangerouslySetInnerHTML={{ __html: post.content }}
              style={{
                lineHeight: '1.8',
                fontSize: '18px'
              }}
            />

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-8">
              {post.tags.map((tag, index) => (
                <span 
                  key={index}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Share buttons */}
            <div className="border-t border-gray-200 pt-8 mb-12">
              <h3 className="text-lg font-semibold mb-4">Share this article</h3>
              <div className="flex gap-4">
                <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer">
                  <i className="ri-twitter-line"></i>
                  Twitter
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-900 transition-colors cursor-pointer">
                  <i className="ri-linkedin-line"></i>
                  LinkedIn
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors cursor-pointer">
                  <i className="ri-whatsapp-line"></i>
                  WhatsApp
                </button>
              </div>
            </div>
          </article>

          {/* Related posts */}
          {relatedPosts.length > 0 && (
            <section className="border-t border-gray-200 pt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-8">Related Articles</h2>
              <div className="grid md:grid-cols-2 gap-8">
                {relatedPosts.map((relatedPost) => (
                  <article key={relatedPost.id} className="group cursor-pointer" onClick={() => navigate(`/blog/${relatedPost.id}`)}>
                    <div className="relative overflow-hidden rounded-lg mb-4">
                      <img 
                        src={relatedPost.image} 
                        alt={relatedPost.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                        {relatedPost.category}
                      </span>
                      <span className="text-gray-500 text-xs">{relatedPost.readTime}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors mb-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-gray-600 text-sm">{relatedPost.excerpt}</p>
                  </article>
                ))}
              </div>
            </section>
          )}

          {/* CTA section */}
          <section className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-center text-white mt-16">
            <h2 className="text-3xl font-bold mb-4">Ready to Build Your High-Converting Funnel?</h2>
            <p className="text-xl mb-6 opacity-90">
              Let our experts create a custom sales funnel that converts visitors into customers.
            </p>
            <Button 
              variant="outline" 
              size="lg" 
              className="border-white text-white hover:bg-white hover:text-blue-600"
              onClick={() => navigate('/contact')}
            >
              <i className="ri-rocket-line mr-2"></i>
              Get Started Today
            </Button>
          </section>
        </div>
      </main>
      
      <Footer />
      
      <style jsx>{`
        .prose h2 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #111827;
          margin-top: 2rem;
          margin-bottom: 1rem;
        }
        .prose h3 {
          font-size: 1.25rem;
          font-weight: 600;
          color: #111827;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .prose p {
          margin-bottom: 1.5rem;
          color: #374151;
        }
        .prose ul {
          margin-bottom: 1.5rem;
          padding-left: 1.5rem;
        }
        .prose li {
          margin-bottom: 0.5rem;
          color: #374151;
        }
      `}</style>
    </div>
  );
}
